import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { LOAN_REQUEST_OFFER_SOURCE } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator.ts';
import { setQueryPosition } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys.ts';

type Props = {
    loanRequestId: string;
    source: LOAN_REQUEST_OFFER_SOURCE;
    id: string;
    leadId: string;
};

export const useOfferStatusMutation = ({ id, leadId, loanRequestId, source }: Props) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const queryKeyGetOffers = queryKeys.getAcceptedOffers(loanRequestId);
    const queryKeyGetOfferCounts = queryKeys.getAcceptedOfferCounts(leadId);
    return useMutation({
        mutationFn: async (status: SchemaLoanRequestResolver_GetAcceptedOffers[number]['status']) => {
            return APICommunicator.sendCommand({
                aggregateId: loanRequestId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.changeOfferStatus,
                payload: {
                    id,
                    source,
                    status,
                },
            });
        },
        onError: (_error) => {
            toast.error(t('OFFER_MANAGEMENT.TOASTS.STATUS_CHANGED_FAILED'));
            queryClient.invalidateQueries({ queryKey: queryKeyGetOfferCounts });
            queryClient.invalidateQueries({ queryKey: queryKeyGetOffers });
        },
        onMutate: async (status: SchemaLoanRequestResolver_GetAcceptedOffers[number]['status']) => {
            queryClient.setQueryData(queryKeyGetOffers, (data: SchemaLoanRequestResolver_GetAcceptedOffers) => {
                const mutatedData = structuredClone(data);
                const oldOffer = mutatedData?.find((offer) => offer.id === id);
                if (oldOffer === undefined) {
                    return data;
                }
                oldOffer.status = status;
                return mutatedData;
            });
        },
        onSuccess: ({ position }) => {
            toast.success(t('OFFER_MANAGEMENT.TOASTS.STATUS_CHANGED_SUCCESS'));
            setQueryPosition(JSON.stringify(queryKeyGetOfferCounts), position);
            queryClient.invalidateQueries({ queryKey: queryKeyGetOfferCounts });
            queryClient.invalidateQueries({ queryKey: queryKeyGetOffers });
        },
    });
};
